import { ANALYTICS_EVENT_NAMES } from "analytics/catalog/events";
import { ScreenKey } from "analytics/catalog/screens";
import { buildCommonEventProps } from "analytics/core/commonProps";
import { trackEvent } from "analytics/core/tracker";

export type SelfCampaignUserFlow = "self-campaign" | "partner";

export type SelfCampaignStepName = "create" | "design" | "activate";
export type SelfCampaignStepNumber = 1 | 2 | 3;

export type SelfCampaignCheckoutSurface =
  | "standard_plans"
  | "partner_paywall";

type SelfCampaignStepMeta = {
  stepName: SelfCampaignStepName;
  stepNumber: SelfCampaignStepNumber;
  screenKey:
    | "self_campaign_create_step"
    | "self_campaign_design_step"
    | "self_campaign_activate_step";
};

const SELF_CAMPAIGN_STEP_META: Record<
  SelfCampaignStepName,
  SelfCampaignStepMeta
> = {
  create: {
    stepName: "create",
    stepNumber: 1,
    screenKey: "self_campaign_create_step",
  },
  design: {
    stepName: "design",
    stepNumber: 2,
    screenKey: "self_campaign_design_step",
  },
  activate: {
    stepName: "activate",
    stepNumber: 3,
    screenKey: "self_campaign_activate_step",
  },
};

export type SelfCampaignAnalyticsContext = {
  pathname: string;
  search: string;
  businessId?: string | null;
  userId?: string | null;
  userEmail?: string | null;
  planType?: string | null;
  currency?: string | null;
  leadSubmissionId?: string | null;
  partner?: string | null;
  userFlow?: SelfCampaignUserFlow;
};

export type SelfCampaignCreateEventProps = {
  business_type?: string | null;
  locations_count?: number | null;
  has_floor_suite?: boolean;
  has_store_address_place_id?: boolean;
  required_fields_completed?: number;
  required_fields_total?: number;
};

export type SelfCampaignDesignEventProps = {
  campaign_type?: string | null;
  social_media_target?: string | null;
  brief_length_chars?: number | null;
};

export type SelfCampaignActivateCheckoutEventProps = {
  plan_id?: string | null;
  plan_name?: string | null;
  plan_price?: string | null;
  is_most_popular?: boolean | null;
  checkout_surface?: SelfCampaignCheckoutSurface | null;
  partner_paywall_variant?: string | null;
};

type SelfCampaignStepProps = {
  step_name: SelfCampaignStepName | null;
  step_number: SelfCampaignStepNumber | null;
};

const resolvePartner = (value?: string | null): string | null => {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const resolveUserFlow = (
  context: SelfCampaignAnalyticsContext,
  resolvedPartner: string | null,
): SelfCampaignUserFlow => {
  if (context.userFlow === "partner" || context.userFlow === "self-campaign") {
    return context.userFlow;
  }

  return resolvedPartner ? "partner" : "self-campaign";
};

const buildSelfCampaignProps = (
  context: SelfCampaignAnalyticsContext,
  screenKey: ScreenKey,
  step: SelfCampaignStepProps,
  extraProps: Record<string, unknown> = {},
): Record<string, unknown> => {
  const commonProps = buildCommonEventProps({
    screenKey,
    pathname: context.pathname,
    search: context.search,
    businessId: context.businessId ?? null,
    userId: context.userId ?? null,
    userEmail: context.userEmail ?? null,
    planType: context.planType ?? null,
    currency: context.currency ?? null,
  });
  const partner = resolvePartner(context.partner);

  return {
    ...commonProps,
    lead_submission_id: context.leadSubmissionId ?? null,
    partner,
    user_flow: resolveUserFlow(context, partner),
    step_name: step.step_name,
    step_number: step.step_number,
    ...extraProps,
  };
};

const buildStepProps = (stepName: SelfCampaignStepName): SelfCampaignStepProps => {
  const stepMeta = SELF_CAMPAIGN_STEP_META[stepName];
  return {
    step_name: stepMeta.stepName,
    step_number: stepMeta.stepNumber,
  };
};

const buildCheckoutStepProps = (): SelfCampaignStepProps => {
  return {
    step_name: null,
    step_number: null,
  };
};

export const trackSelfCampaignCreateStepViewed = (
  context: SelfCampaignAnalyticsContext,
  props: SelfCampaignCreateEventProps = {},
): void => {
  trackEvent(
    ANALYTICS_EVENT_NAMES.selfCampaignCreateStepViewed,
    buildSelfCampaignProps(
      context,
      SELF_CAMPAIGN_STEP_META.create.screenKey,
      buildStepProps("create"),
      props,
    ),
  );
};

export const trackSelfCampaignCreateContinueClicked = (
  context: SelfCampaignAnalyticsContext,
  props: SelfCampaignCreateEventProps = {},
): void => {
  trackEvent(
    ANALYTICS_EVENT_NAMES.selfCampaignCreateContinueClicked,
    buildSelfCampaignProps(
      context,
      SELF_CAMPAIGN_STEP_META.create.screenKey,
      buildStepProps("create"),
      props,
    ),
  );
};

export const trackSelfCampaignCreateProgressSaved = (
  context: SelfCampaignAnalyticsContext,
  props: SelfCampaignCreateEventProps = {},
): void => {
  trackEvent(
    ANALYTICS_EVENT_NAMES.selfCampaignCreateProgressSaved,
    buildSelfCampaignProps(
      context,
      SELF_CAMPAIGN_STEP_META.create.screenKey,
      buildStepProps("create"),
      props,
    ),
  );
};

export const trackSelfCampaignCreateProgressSaveFailed = (
  context: SelfCampaignAnalyticsContext,
  props: SelfCampaignCreateEventProps = {},
): void => {
  trackEvent(
    ANALYTICS_EVENT_NAMES.selfCampaignCreateProgressSaveFailed,
    buildSelfCampaignProps(
      context,
      SELF_CAMPAIGN_STEP_META.create.screenKey,
      buildStepProps("create"),
      props,
    ),
  );
};

export const trackSelfCampaignDesignStepViewed = (
  context: SelfCampaignAnalyticsContext,
  props: SelfCampaignDesignEventProps = {},
): void => {
  trackEvent(
    ANALYTICS_EVENT_NAMES.selfCampaignDesignStepViewed,
    buildSelfCampaignProps(
      context,
      SELF_CAMPAIGN_STEP_META.design.screenKey,
      buildStepProps("design"),
      props,
    ),
  );
};

export const trackSelfCampaignDesignContinueClicked = (
  context: SelfCampaignAnalyticsContext,
  props: SelfCampaignDesignEventProps = {},
): void => {
  trackEvent(
    ANALYTICS_EVENT_NAMES.selfCampaignDesignContinueClicked,
    buildSelfCampaignProps(
      context,
      SELF_CAMPAIGN_STEP_META.design.screenKey,
      buildStepProps("design"),
      props,
    ),
  );
};

export const trackSelfCampaignDesignProgressSaved = (
  context: SelfCampaignAnalyticsContext,
  props: SelfCampaignDesignEventProps = {},
): void => {
  trackEvent(
    ANALYTICS_EVENT_NAMES.selfCampaignDesignProgressSaved,
    buildSelfCampaignProps(
      context,
      SELF_CAMPAIGN_STEP_META.design.screenKey,
      buildStepProps("design"),
      props,
    ),
  );
};

export const trackSelfCampaignDesignProgressSaveFailed = (
  context: SelfCampaignAnalyticsContext,
  props: SelfCampaignDesignEventProps = {},
): void => {
  trackEvent(
    ANALYTICS_EVENT_NAMES.selfCampaignDesignProgressSaveFailed,
    buildSelfCampaignProps(
      context,
      SELF_CAMPAIGN_STEP_META.design.screenKey,
      buildStepProps("design"),
      props,
    ),
  );
};

export const trackSelfCampaignActivateStepViewed = (
  context: SelfCampaignAnalyticsContext,
): void => {
  trackEvent(
    ANALYTICS_EVENT_NAMES.selfCampaignActivateStepViewed,
    buildSelfCampaignProps(
      context,
      SELF_CAMPAIGN_STEP_META.activate.screenKey,
      buildStepProps("activate"),
    ),
  );
};

export const trackSelfCampaignActivateCheckoutClicked = (
  context: SelfCampaignAnalyticsContext,
  props: SelfCampaignActivateCheckoutEventProps = {},
): void => {
  trackEvent(
    ANALYTICS_EVENT_NAMES.selfCampaignActivateCheckoutClicked,
    buildSelfCampaignProps(
      context,
      SELF_CAMPAIGN_STEP_META.activate.screenKey,
      buildStepProps("activate"),
      props,
    ),
  );
};

export const trackSelfCampaignActivateCheckoutRedirectSucceeded = (
  context: SelfCampaignAnalyticsContext,
  props: SelfCampaignActivateCheckoutEventProps = {},
): void => {
  trackEvent(
    ANALYTICS_EVENT_NAMES.selfCampaignActivateCheckoutRedirectSucceeded,
    buildSelfCampaignProps(
      context,
      SELF_CAMPAIGN_STEP_META.activate.screenKey,
      buildStepProps("activate"),
      props,
    ),
  );
};

export const trackSelfCampaignActivateCheckoutRedirectFailed = (
  context: SelfCampaignAnalyticsContext,
  props: SelfCampaignActivateCheckoutEventProps = {},
): void => {
  trackEvent(
    ANALYTICS_EVENT_NAMES.selfCampaignActivateCheckoutRedirectFailed,
    buildSelfCampaignProps(
      context,
      SELF_CAMPAIGN_STEP_META.activate.screenKey,
      buildStepProps("activate"),
      props,
    ),
  );
};

export const trackSelfCampaignCheckoutSuccess = (
  context: SelfCampaignAnalyticsContext,
  props: SelfCampaignActivateCheckoutEventProps = {},
): void => {
  trackEvent(
    ANALYTICS_EVENT_NAMES.selfCampaignCheckoutSuccess,
    buildSelfCampaignProps(
      context,
      "self_campaign_checkout",
      buildCheckoutStepProps(),
      props,
    ),
  );
};

export const trackSelfCampaignCheckoutFailed = (
  context: SelfCampaignAnalyticsContext,
  props: SelfCampaignActivateCheckoutEventProps = {},
): void => {
  trackEvent(
    ANALYTICS_EVENT_NAMES.selfCampaignCheckoutFailed,
    buildSelfCampaignProps(
      context,
      "self_campaign_checkout",
      buildCheckoutStepProps(),
      props,
    ),
  );
};

export const SELF_CAMPAIGN_PENDING_CHECKOUT_STORAGE_KEY =
  "pending_self_campaign_checkout";

const SELF_CAMPAIGN_PENDING_CHECKOUT_VERSION = 1;
const SELF_CAMPAIGN_PENDING_CHECKOUT_TTL_MS = 1000 * 60 * 60 * 24;

export type PendingSelfCampaignCheckoutContext = {
  version: number;
  createdAtMs: number;
  leadSubmissionId: string;
  partner: string | null;
  userFlow: SelfCampaignUserFlow;
  planId: string | null;
  planName: string | null;
  planPrice: string | null;
  isMostPopular: boolean | null;
  checkoutSurface: SelfCampaignCheckoutSurface;
};

export type SavePendingSelfCampaignCheckoutInput = {
  leadSubmissionId: string;
  partner?: string | null;
  userFlow?: SelfCampaignUserFlow;
  planId?: string | null;
  planName?: string | null;
  planPrice?: string | null;
  isMostPopular?: boolean | null;
  checkoutSurface: SelfCampaignCheckoutSurface;
};

const hasLocalStorage = (): boolean => {
  return (
    typeof window !== "undefined" && typeof window.localStorage !== "undefined"
  );
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== "object") {
    return null;
  }

  return value as Record<string, unknown>;
};

const normalizeNullableString = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const isValidCheckoutSurface = (
  value: unknown,
): value is SelfCampaignCheckoutSurface => {
  return value === "standard_plans" || value === "partner_paywall";
};

const isValidUserFlow = (value: unknown): value is SelfCampaignUserFlow => {
  return value === "self-campaign" || value === "partner";
};

export const savePendingSelfCampaignCheckoutContext = (
  input: SavePendingSelfCampaignCheckoutInput,
): void => {
  if (!hasLocalStorage()) {
    return;
  }

  const leadSubmissionId = normalizeNullableString(input.leadSubmissionId);
  if (!leadSubmissionId) {
    return;
  }

  const partner = resolvePartner(input.partner);
  const userFlow =
    input.userFlow && isValidUserFlow(input.userFlow)
      ? input.userFlow
      : partner
        ? "partner"
        : "self-campaign";

  const payload: PendingSelfCampaignCheckoutContext = {
    version: SELF_CAMPAIGN_PENDING_CHECKOUT_VERSION,
    createdAtMs: Date.now(),
    leadSubmissionId,
    partner,
    userFlow,
    planId: normalizeNullableString(input.planId),
    planName: normalizeNullableString(input.planName),
    planPrice: normalizeNullableString(input.planPrice),
    isMostPopular:
      typeof input.isMostPopular === "boolean" ? input.isMostPopular : null,
    checkoutSurface: input.checkoutSurface,
  };

  window.localStorage.setItem(
    SELF_CAMPAIGN_PENDING_CHECKOUT_STORAGE_KEY,
    JSON.stringify(payload),
  );
};

export const clearPendingSelfCampaignCheckoutContext = (): void => {
  if (!hasLocalStorage()) {
    return;
  }

  window.localStorage.removeItem(SELF_CAMPAIGN_PENDING_CHECKOUT_STORAGE_KEY);
};

export const readPendingSelfCampaignCheckoutContext =
  (): PendingSelfCampaignCheckoutContext | null => {
    if (!hasLocalStorage()) {
      return null;
    }

    const serialized = window.localStorage.getItem(
      SELF_CAMPAIGN_PENDING_CHECKOUT_STORAGE_KEY,
    );
    if (!serialized) {
      return null;
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(serialized);
    } catch (_error) {
      clearPendingSelfCampaignCheckoutContext();
      return null;
    }

    const record = asRecord(parsed);
    if (!record) {
      clearPendingSelfCampaignCheckoutContext();
      return null;
    }

    if (record.version !== SELF_CAMPAIGN_PENDING_CHECKOUT_VERSION) {
      clearPendingSelfCampaignCheckoutContext();
      return null;
    }

    const createdAtMs =
      typeof record.createdAtMs === "number" ? record.createdAtMs : 0;
    const isExpired = Date.now() - createdAtMs > SELF_CAMPAIGN_PENDING_CHECKOUT_TTL_MS;
    if (createdAtMs <= 0 || isExpired) {
      clearPendingSelfCampaignCheckoutContext();
      return null;
    }

    const leadSubmissionId = normalizeNullableString(record.leadSubmissionId);
    const partner = resolvePartner(record.partner as string | null | undefined);
    const checkoutSurface = record.checkoutSurface;
    const userFlow = isValidUserFlow(record.userFlow)
      ? record.userFlow
      : partner
        ? "partner"
        : "self-campaign";

    if (!leadSubmissionId || !isValidCheckoutSurface(checkoutSurface)) {
      clearPendingSelfCampaignCheckoutContext();
      return null;
    }

    return {
      version: SELF_CAMPAIGN_PENDING_CHECKOUT_VERSION,
      createdAtMs,
      leadSubmissionId,
      partner,
      userFlow,
      planId: normalizeNullableString(record.planId),
      planName: normalizeNullableString(record.planName),
      planPrice: normalizeNullableString(record.planPrice),
      isMostPopular:
        typeof record.isMostPopular === "boolean" ? record.isMostPopular : null,
      checkoutSurface,
    };
  };
